'use client'

import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'

export default function TranslationWorkshop() {
  const { t, language } = useLanguage()

  const workshopLevels = [
    {
      level: 'beginner',
      gradient: 'from-[#B9AE9A] to-[#8C4233]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      level: 'intermediate',
      gradient: 'from-[#506B72] to-[#B9AE9A]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      level: 'advanced',
      gradient: 'from-[#8C4233] to-[#506B72]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    }
  ]

  const features = [
    {
      key: 'mentorship',
      gradient: 'from-[#B9AE9A] to-[#8C4233]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      key: 'collaboration',
      gradient: 'from-[#506B72] to-[#B9AE9A]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      key: 'publication',
      gradient: 'from-[#8C4233] to-[#506B72]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      key: 'community',
      gradient: 'from-[#B9AE9A] to-[#506B72]',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
        </svg>
      )
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-16 pb-16 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl font-bauhaus text-white tracking-tight mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {t('bpf.workshop.title')}
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-white/70 max-w-4xl mx-auto leading-relaxed"
            >
              {t('bpf.workshop.intro')}
            </motion.p>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* 诗人合作作品选 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-24"
        >
          {/* 作品选网格 - 3列不对称布局 */}
          <div className="grid grid-cols-3 grid-rows-2 gap-8 h-[300px]">
            
            {/* 第一列 - 上方卡片 */}
            <Link to="/Qiaodong/BPF/workshop/Group_2" className="contents">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-[#B9AE9A] to-[#B9AE9A]/80 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
              style={{borderWidth: '4px', borderRadius: '24px', borderColor: '#B9AE9A'}}
            >
              <div className="flex flex-col justify-center h-full" style={{borderRadius: '24px'}}>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 text-center">子虚 & Michel</h3>
                  <p className="text-white/90 leading-relaxed text-center">{t('bpf.workshop.collaborations.works_selection')}</p>
                </div>
              </div>
            </motion.div>
            </Link>

            {/* 第二列 - 中间大卡片（跨两行） */}
            <Link to="/Qiaodong/BPF/workshop/Group_1" className="contents">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-[#506B72]/90 to-[#B9AE9A]/70 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 row-span-2 flex flex-col justify-center"
              style={{borderWidth: '4px', borderRadius: '24px', borderColor: '#506B72'}}
            >
              <div className="flex flex-col justify-center h-full" style={{borderRadius: '24px'}}>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4 text-center">李靖 & Yevgenniy</h3>
                  <p className="text-white/90 leading-relaxed text-center">{t('bpf.workshop.collaborations.works_selection')}</p>
                </div>
              </div>
            </motion.div>
            </Link>

            {/* 第三列 - 上方卡片 */}
            <Link to="/Qiaodong/BPF/workshop/Group_4" className="contents">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-[#8C4233] to-[#506B72]/80 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
              style={{borderWidth: '4px', borderRadius: '24px', borderColor: '#8C4233'}}
            >
              <div className="flex flex-col justify-center h-full" style={{borderRadius: '24px'}}>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 text-center">周幼安 & Lara</h3>
                  <p className="text-white/90 leading-relaxed text-center">{t('bpf.workshop.collaborations.works_selection')}</p>
                </div>
              </div>
            </motion.div>
            </Link>

            {/* 第一列 - 下方卡片 */}
            <Link to="/Qiaodong/BPF/workshop/Group_3" className="contents">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gradient-to-br from-[#B9AE9A]/90 to-[#8C4233]/70 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
              style={{borderWidth: '4px', borderRadius: '24px', borderColor: '#B9AE9A'}}
            >
              <div className="flex flex-col justify-center h-full" style={{borderRadius: '24px'}}>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 text-center">叙矣 & Matyas</h3>
                  <p className="text-white/90 leading-relaxed text-center">{t('bpf.workshop.collaborations.works_selection')}</p>
                </div>
              </div>
            </motion.div>
            </Link>

            {/* 第三列 - 下方卡片 */}
            <Link to="/Qiaodong/BPF/workshop/Group_5" className="contents">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-gradient-to-br from-[#506B72] to-[#8C4233]/70 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
              style={{borderWidth: '4px', borderRadius: '24px', borderColor: '#506B72'}}
            >
              <div className="flex flex-col justify-center h-full" style={{borderRadius: '24px'}}>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3 text-center">喻折 & Janin</h3>
                  <p className="text-white/90 leading-relaxed text-center">{t('bpf.workshop.collaborations.works_selection')}</p>
                </div>
              </div>
            </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 